import { useCallback, useEffect, useState } from 'react';
import { bffFetch, BffError } from '../helpers/bff';
import { getSupabase, isSupabaseConfigured } from '../client';

export interface NotificacionComentario {
  /** Discriminator. 'comentario' = mensaje en parcela. 'estado_cambio' =
   *  transición de estado de gestión. Default 'comentario' por
   *  compatibilidad con consumidores antiguos. */
  type?: 'comentario' | 'estado_cambio';
  id: string;
  parcela_id: string;
  parcela_codigo: string | null;
  parcela_num: string | number | null;
  cuerpo: string;
  created_at: string;
  autor_id: string;
  autor_rol: string | null;
  autor_nombre: string | null;
  /** Solo en type='estado_cambio'. */
  estado_old?: string | null;
  estado_new?: string | null;
}

interface UseNotificacionesResult {
  notifications: NotificacionComentario[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Lista los comentarios sin leer del usuario actual cross-parcela. Pensado
 * para un dropdown en topbar.
 *
 * Estrategia híbrida:
 *   - Realtime: suscripción a INSERT/UPDATE de `comentarios` (mismo channel
 *     que useComentarios para que un solo cambio refresque ambos lugares).
 *   - Polling de fallback cada 60s: por si el WebSocket se cae sin notificar
 *     (Safari background suspension, networking flaky).
 *   - Refresh on visibilitychange: si el user volvió al tab tras minutos
 *     fuera, hacemos refetch para que el badge esté al día.
 */
export function useNotificacionesComentarios(): UseNotificacionesResult {
  const [notifications, setNotifications] = useState<NotificacionComentario[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!isSupabaseConfigured()) return;
    try {
      const json = await bffFetch<{ notifications: NotificacionComentario[] }>(
        '/v1/comentarios/notifications',
      );
      setNotifications(json.notifications ?? []);
      setError(null);
    } catch (e) {
      // 401/403: user sin sesión o sin tenant — no es error que mostrar.
      if (e instanceof BffError && (e.status === 401 || e.status === 403)) {
        setNotifications([]);
        setError(null);
      } else {
        setError(e as Error);
      }
    }
  }, []);

  // Mount: fetch inicial + setup poll + visibility + realtime.
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    setLoading(true);
    void fetchOnce().finally(() => setLoading(false));

    const POLL_MS = 60_000;
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void fetchOnce();
    }, POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void fetchOnce();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Realtime: cualquier cambio en comentarios O en gestiones (estado del
    // semáforo) refresca el dropdown. gestiones cubre el caso "Parcela X
    // pasó a contratada" — ya está incluida en la publicación realtime
    // (mig realtime_publish_gestiones_expedientes_visitas).
    const sb = getSupabase();
    const chComentarios = sb
      .channel('comentarios:notifications')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'comentarios' }, () => {
        void fetchOnce();
      })
      .subscribe();
    const chGestiones = sb
      .channel('gestiones:notifications')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gestiones' }, () => {
        void fetchOnce();
      })
      .subscribe();

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      void sb.removeChannel(chComentarios);
      void sb.removeChannel(chGestiones);
    };
  }, [fetchOnce]);

  return { notifications, loading, error, refetch: fetchOnce };
}
